import React, { useState, useEffect, useCallback } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { IconHome, IconUsers, IconCog, IconBell, IconShield } from 'src/icons';
import { ADMIN_NAV, ADMIN_PATH, ADMIN_USERS_PATH, ADMIN_ADMINS_PATH, ADMIN_NOTIFICATIONS_PATH } from './constants';
import { ADMIN_DATA_CHANGED_EVENT } from './AdminContext';
import { getAdminCountsApi } from 'src/services/api';

const STORAGE_USER = 'flower_power_notes_user';

const adminNavIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  dashboard: IconHome,
  users: IconUsers,
  settings: IconCog,
  notifications: IconBell,
  admins: IconShield,
};

const AdminLayout: React.FC = () => {
  const location = useLocation();
  const [counts, setCounts] = useState<{ users: number; admins: number; notifications: number } | null>(null);

  const refreshCounts = useCallback(() => {
    const user = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem(STORAGE_USER) || '{}') : {};
    const authToken = user?.AuthToken;
    if (!authToken) return;
    getAdminCountsApi(authToken).then((res) => {
      if (!res.error) setCounts({ users: res.users ?? 0, admins: res.admins ?? 0, notifications: res.notifications ?? 0 });
    });
  }, []);

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  useEffect(() => {
    const handler = () => refreshCounts();
    window.addEventListener(ADMIN_DATA_CHANGED_EVENT, handler);
    return () => window.removeEventListener(ADMIN_DATA_CHANGED_EVENT, handler);
  }, [refreshCounts]);

  const getBadgeCount = (path: string): number | null => {
    if (!counts) return null;
    if (path === ADMIN_USERS_PATH) return counts.users;
    if (path === ADMIN_ADMINS_PATH) return counts.admins;
    if (path === ADMIN_NOTIFICATIONS_PATH) return counts.notifications;
    return null;
  };

  return (
    <div className="flex flex-1 w-full min-h-0 overflow-hidden">
      <aside className="top-20 bottom-0 left-0 z-30 fixed bg-white py-6 pr-4 sm:pr-6 pl-4 sm:pl-6 border-gray-200 border-r w-56 lg:w-64 overflow-y-auto">
        <div className="flex flex-col gap-1">
            {ADMIN_NAV.map((item) => {
              const isActive =
                item.path === ADMIN_PATH ? location.pathname === ADMIN_PATH : location.pathname.startsWith(item.path);
              const badgeCount = getBadgeCount(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`inline-flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors shrink-0 ${
                    isActive
                      ? 'bg-sky-50 text-sky-700 ring-1 ring-sky-200'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  {(() => {
                    const IconComponent = adminNavIcons[item.icon];
                    return IconComponent ? <IconComponent className="w-5 h-5 shrink-0" /> : null;
                  })()}
                  <span className="flex-1 min-w-0 truncate">{item.label}</span>
                  {badgeCount != null && (
                    <span
                      className={`shrink-0 min-w-[1.25rem] h-5 px-1.5 rounded-full flex items-center justify-center text-xs font-semibold ${
                        isActive ? 'bg-sky-200 text-sky-800' : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {badgeCount > 999 ? '999+' : badgeCount}
                    </span>
                  )}
                </Link>
              );
            })}
        </div>
      </aside>
      <main className="flex flex-col flex-1 ml-56 lg:ml-64 px-1.5 py-1 min-w-0 max-w-[1200px] min-h-0 overflow-hidden">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
